import React, { type ReactNode, useCallback } from 'react';

import { styled } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { overflowShadow, type OverflowShadowProps } from '@atlaskit/editor-common/ui';
import { token } from '@atlaskit/tokens';
import { useExtensionsState } from '@atlassian/company-hub-common';

import { NavigationStyle } from '../../../types';
import { CarouselNavigation } from '../carousel-navigation';

import { messages } from './messages';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Frame = styled.div<{
	isLastFrame: boolean;
}>({
	// can't use gap due to before/after shadow setinels also
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: ({ isLastFrame }) => (isLastFrame ? token('space.0') : token('space.150')),
	// Margins on the smart cards make the focus ring on any container but the smart card's container looks off.
	// Hiding focus ring on the container and outlining the children, {smart/error}-cards instead.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus-visible': {
		outline: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus-visible div[data-smart-link-container], :focus-visible .frame-small-error-state': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
	},
});

type FrameStripComponentProps = {
	extensionLocalId?: string;
	frameIds: string[];
	frames: ReactNode[];
	isAutoplayEnabled?: boolean;
	isCarouselVisible: () => boolean;
	isHovered?: boolean;
	isInViewMode: boolean;
	navigationStyle?: NavigationStyle;
	selectedHeroFrameIndex: number;
	setSelectedHeroFrameIndex: (index: number) => void;
	width?: number;
} & OverflowShadowProps;

export const FrameStripComponent = ({
	isInViewMode,
	frames,
	frameIds,
	selectedHeroFrameIndex,
	isHovered = false,
	extensionLocalId = 'default-extension-local-id',
	isAutoplayEnabled = false,
	isCarouselVisible,
	navigationStyle = NavigationStyle.THUMBNAILS,
	setSelectedHeroFrameIndex,
	width,
}: FrameStripComponentProps): React.JSX.Element => {
	const intl = useIntl();
	const [_state, { setIsInAutoFocusMode, setSelectedToEditCardId }] = useExtensionsState();

	const handleUpdateCardOnNavigation = useCallback(
		(index: number) => {
			setSelectedHeroFrameIndex(index);
			if (!isInViewMode) {
				setIsInAutoFocusMode(true);
				setSelectedToEditCardId(frameIds[index]);
			}
		},
		[
			setSelectedHeroFrameIndex,
			isInViewMode,
			setSelectedToEditCardId,
			setIsInAutoFocusMode,
			frameIds,
		],
	);

	const handleSelectFrame = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | undefined, index: number) => {
			event?.stopPropagation();
			handleUpdateCardOnNavigation(index);
		},
		[handleUpdateCardOnNavigation],
	);

	const handleKeyDownAsButtonClick = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
			event.stopPropagation();

			// Keyboard support for button: https://www.w3.org/TR/wai-aria-practices/examples/button/button.html
			// event.key spec: https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
			const ACTIVATE_BUTTON_KEYS = ['Enter', 'Spacebar', ' '];

			if (ACTIVATE_BUTTON_KEYS.includes(event.key)) {
				event.preventDefault();
				handleSelectFrame(undefined, index);
			}
		},
		[handleSelectFrame],
	);

	const renderFrame = useCallback(
		(card: ReactNode, index: number) => {
			return (
				<Frame
					key={frameIds[index]}
					data-testid="frame-strip-frame"
					aria-current={selectedHeroFrameIndex === index}
					aria-label={intl.formatMessage(messages.frameLabel, {
						positionInSet: index + 1,
						setSize: frames.length,
					})}
					onClick={(event) => handleSelectFrame(event, index)}
					onKeyDown={(event) => handleKeyDownAsButtonClick(event, index)}
					isLastFrame={index === frames.length - 1}
				>
					{card}
				</Frame>
			);
		},
		[intl, frames, frameIds, selectedHeroFrameIndex, handleSelectFrame, handleKeyDownAsButtonClick],
	);

	return (
		<CarouselNavigation
			width={width}
			frames={frames}
			frameIds={frameIds}
			extensionLocalId={extensionLocalId}
			renderFrame={renderFrame}
			handleNavigationFrameUpdate={handleUpdateCardOnNavigation}
			selectedHeroFrameIndex={selectedHeroFrameIndex}
			isHovered={isHovered}
			isCarouselVisible={isCarouselVisible}
			isAutoplayEnabled={isAutoplayEnabled}
			navigationStyle={navigationStyle}
		/>
	);
};

export const FrameStrip: React.ComponentType<FrameStripComponentProps> = overflowShadow(
	FrameStripComponent,
	{
		overflowSelector: '.frame-strip-overflow',
		useShadowObserver: true,
	},
);
